// @ts-nocheck
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import { headerHideForMobileStyles } from "@store/Themes/common/mixins/header";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Starter Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // Starter Custom Code
    wrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    navWrapper?: InjectableCss;
    bottomContentWrapper?: InjectableCss;
    printWrapper?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    skipToContent: {
        skipToContent: {
            css: css`
                &:focus {
                    z-index: 4;
                }
            `,
        },
    },
    contentWrapper: {
        css: css`
            position: relative;
            z-index: 3;
        `,
    },
    navWrapper: {
        css: css`
            ${headerHideForMobileStyles()}
            position: relative;
            z-index: 2;
        `,
    },
    bottomContentWrapper: {
        css: css`
            position: relative;
            z-index: 1;
        `,
    },
    printWrapper: {
        css: css`
            display: none;

            img {
                width: auto;
                height: ${pxToRem(70)};
            }

            @media print {
                display: block;
            }
        `,
    },
};

const styles = headerStyles;

const Header = ({ id, siteName, session, toaster, history, myAccountPageUrl, dismissRfqUpdates }: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* Starter Removed - siteName */}
            <StyledWrapper {...styles.printWrapper}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* Starter Custom Code - Start */}
                    <StyledWrapper {...styles.wrapper}>
                        <StyledWrapper {...styles.contentWrapper}>
                            <Zone contentId={id} zoneName="Content" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.navWrapper} data-zone-single>
                            <Zone contentId={id} zoneName="Nav" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.bottomContentWrapper}>
                            <Zone contentId={id} zoneName="ContentBottom" />
                        </StyledWrapper>
                    </StyledWrapper>
                    {/* Starter Custom Code - End */}
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
